<template>
  <div class="page-slots">
    <header class="slots-header">
      <div class="slots-header-inner">
        <div class="slots-provider-block">
          <span class="slots-provider-eyebrow">{{ $t('home.arcade_game') }}</span>
          <h1 class="slots-provider-title">{{ displayProvider }}</h1>
        </div>
        <div class="slots-search-wrap">
          <i class="fas fa-search slots-search-icon" aria-hidden="true"></i>
          <input
            v-model="name"
            type="search"
            class="slots-search"
            :placeholder="$t('home.search_game')"
            autocomplete="off"
            enterkeyhint="search"
          >
        </div>
      </div>
    </header>

    <main class="slots-main">
      <div v-if="displayGames.length === 0" class="slots-empty">
        <i class="fas fa-dice slots-empty-icon" aria-hidden="true"></i>
        <p class="slots-empty-text">{{ emptyMessage }}</p>
      </div>
      <div v-else class="slots-grid">
        <button
          v-for="(game, index) in displayGames"
          :key="`${game.gameID}-${index}`"
          type="button"
          class="slots-card"
          @click="gameInit(game)"
        >
          <div class="slots-card-thumb">
            <GameThumbImg
              :src="imageLinkGenerate(game.gameID, game.provider)"
              :alt="displayGameName(game)"
            />
          </div>
          <div class="slots-card-meta">
            <span class="slots-card-name">{{ displayGameName(game) }}</span>
          </div>
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import GameThumbImg from '@/components/GameThumbImg.vue';
import {
  getGameUrlFromGamesUrlResponse,
  getGameUrlMissingLaunchBody,
} from '@/utils/gameUrlApiError';
import { showGameUrlErrorDialog, showGameUrlClientError } from '@/utils/showGameUrlErrorDialog';

export default {
  components: { GameThumbImg },

  data() {
    return {
      games: [],
      rows: 0,
      name: '',
    };
  },

  computed: {
    displayProvider() {
      return this.$route.query.provider || 'Arcade';
    },
    displayGames() {
      const list = Array.isArray(this.games) ? this.games : [];
      const q = (this.name || '').trim().toLowerCase();
      if (!q) return list;
      return list.filter((g) => (g.gameName || '').toLowerCase().includes(q));
    },
    emptyMessage() {
      return this.$store.state.language === 'en'
        ? 'No games match your search.'
        : 'ရှာဖွေမှုနဲ့ ကိုက်ညီသော ဂိမ်းမရှိပါ။';
    },
  },

  watch: {
    '$route.query.provider'() {
      this.name = '';
      this.fetchGames();
    },
  },

  methods: {
    ...mapActions(['fetchUser']),

    displayGameName(game) {
      const raw = (game && game.gameName) ? String(game.gameName).trim() : '';
      if (!raw) return '';
      const candidates = [
        this.$route.query.provider,
        game.provider,
      ]
        .filter(Boolean)
        .map((p) => String(p).trim())
        .filter((p, i, a) => a.indexOf(p) === i);

      let s = raw;
      for (const p of candidates) {
        if (!p) continue;
        const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        s = s.replace(new RegExp(`^${esc}\\s*[|\\-–—:]\\s*`, 'i'), '');
        s = s.replace(new RegExp(`\\s*[|\\-–—:]\\s*${esc}$`, 'i'), '');
        s = s.replace(new RegExp(`^${esc}\\s+`, 'i'), '');
        s = s.replace(new RegExp(`\\s+${esc}$`, 'i'), '');
        s = s.replace(new RegExp(`^\\[${esc}\\]\\s*`, 'i'), '');
      }
      s = s.trim();
      return s || raw;
    },

    imageLinkGenerate(gameId, type) {
      if (type === 'Jili') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/jili/${gameId}.webp`;
      }
      if (type === 'Live22') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/live22/${gameId}.webp`;
      }
      if (type === 'Spade') {
        return `https://merchantapi.silverkirin88.com/thumbnail/en_US/${gameId}.jpg`;
      }
      if (type === 'Fastspin') {
        return `http://api-egame-staging.fsuat.com/thumbnail/en_US/${gameId}.jpg`;
      }
      if (type === 'Playstar') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/playstar/${gameId}.webp`;
      }
      if (type === 'FaChai') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/fachai/${gameId}.webp`;
      }
      if (type === 'PGSoft') {
        return `https://space-tech.sgp1.cdn.digitaloceanspaces.com/slot-images/pgsoft/pgsoft_${gameId}.webp`;
      }
      if (type === 'JOKER' || type === 'Joker') {
        return `https://space-tech.sgp1.cdn.digitaloceanspaces.com/slot-images/joker/${gameId}.webp`;
      }
      if (type === '5G') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/5g/${gameId}.webp`;
      }
      if (type === 'KA') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/newKA/${gameId}.webp`;
      }
      if (type === 'AceWin') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/acewin/${gameId}_EN.webp`;
      }
      if (type === 'JDB') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/jdb/${gameId}.webp`;
      }
      if (type === 'Buffalo') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/pp/${gameId}.webp`;
      }
      if (type === 'HotDog') {
        return `https://res.hotdog-gaming.com/banners/380x500/${gameId}.png`;
      }
      if (type === 'Rich') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/rich/${gameId}.webp`;
      }
      if (type === 'Rich88') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/rich88/${gameId}.webp`;
      }
      if (type === 'CQ9' || type === 'cq9' || type === 'SexySlot') {
        return `https://space-tech.sgp1.cdn.digitaloceanspaces.com/slot-images/cq9/cq9_${gameId}.webp`;
      }
      if (type === 'BTG') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/btg/${gameId}.webp`;
      }
      if (type === '2J') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/2j/${gameId}.webp`;
      }
      if (type === 'VA') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/va/${gameId}_en.webp`;
      }
      if (type === 'BNG'){
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/bng/${gameId}.webp`;
      }
      if (type === 'AfricanBuffalo') {
        return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/afb/${gameId}.webp`;
      }

      return `https://cdn.myanmarshankoeme.com/build/assets/img/bf688/pp/${gameId}.webp`;
    },

    async fetchGames() {
      try {
        const res = await axios.get('/games', {
          params: {
            provider: this.$route.query.provider,
          },
        });
        this.games = res.data.data;
        this.games = this.games.filter((game) => game.gameTypeID.includes('ac'));
      } catch (error) {
        console.log(error);
      }
    },

    async gameInit(game) {
      try {
        this.$store.commit('setLoading', true);
        const authUser = await this.fetchUser();
        if (!authUser) {
          this.$router.push('/auth/login');
        }

        const res = await axios.get('/games/url', {
          params: {
            gameID: game.gameID,
            provider: this.$route.query.provider,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        const launchUrl = getGameUrlFromGamesUrlResponse(res);
        if (!launchUrl) {
          this.$store.commit('setLoading', false);
          const lang = this.$store.state.language === 'en' ? 'en' : 'mm';
          showGameUrlClientError(getGameUrlMissingLaunchBody(res, lang), lang);
          return;
        }

        if (this.$route.query.provider === 'PGSoft') {
          const newPage = window.open('', '_self');

          if (newPage) {
            newPage.document.open();
            newPage.document.write(launchUrl);
            newPage.document.close();
          }
        } else {
          window.top.location.href = launchUrl;
        }

        this.$store.commit('setIframeURL', launchUrl);
        this.$router.push('/iframeURL');
        this.$store.commit('setLoading', false);
      } catch (error) {
        this.$store.commit('setLoading', false);
        showGameUrlErrorDialog(
          error,
          this.$store.state.language === 'en' ? 'en' : 'mm',
          this.$store.state.language === 'en'
            ? 'Unable to open the game.'
            : 'ဂိမ်းဖွင့်၍မရပါ။',
        );
      }
    },
  },

  async mounted() {
    this.$store.commit('setLoading', true);
    await this.fetchGames();
    this.$store.commit('setLoading', false);
  },
};
</script>

<style scoped>
.page-slots {
  min-height: 100vh;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 6rem;
  background: linear-gradient(
    180deg,
    var(--slot-bg-raised, #11100e) 0%,
    var(--slot-bg-deep, #0a0908) 45%,
    #080706 100%
  );
  -webkit-overflow-scrolling: touch;
}

.slots-header {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 0.65rem 0.5rem 0.75rem;
  background: linear-gradient(180deg, rgba(14, 12, 10, 0.98) 0%, rgba(10, 9, 8, 0.94) 100%);
  border-bottom: 1px solid var(--slot-brass, rgba(198, 165, 86, 0.45));
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 220, 150, 0.06);
}

.slots-header-inner {
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.slots-provider-block {
  text-align: center;
}

.slots-provider-eyebrow {
  display: block;
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--slot-gold-soft, #b09a5c);
  margin-bottom: 0.2rem;
}

.slots-provider-title {
  font-family: 'Cinzel', 'Times New Roman', serif;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--slot-gold, #d4b968);
  margin: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
  line-height: 1.25;
  word-break: break-word;
}

.slots-search-wrap {
  position: relative;
  width: 100%;
}

.slots-search-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--slot-gold-soft, #b09a5c);
  opacity: 0.85;
  pointer-events: none;
}

.slots-search {
  width: 100%;
  border: 1px solid var(--slot-brass, rgba(198, 165, 86, 0.45));
  background: rgba(22, 19, 16, 0.85);
  color: #e8e4dc;
  border-radius: 999px;
  font-size: 0.82rem;
  padding: 0.55rem 0.85rem 0.55rem 2.25rem;
  box-sizing: border-box;
  outline: none;
  box-shadow: inset 0 1px 0 rgba(255, 220, 150, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.slots-search::placeholder {
  color: rgba(200, 192, 175, 0.55);
}

.slots-search:focus {
  border-color: var(--slot-brass-bright, rgba(232, 201, 120, 0.85));
  box-shadow:
    inset 0 1px 0 rgba(255, 220, 150, 0.08),
    0 0 0 2px rgba(212, 185, 104, 0.12);
}

.slots-main {
  padding-top: 0.5rem;
  max-width: 100%;
  margin: 0 auto;
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.4rem 0.3rem;
  padding: 0 0.15rem 1rem;
}

.slots-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.slots-card:active .slots-card-thumb {
  transform: scale(0.97);
}

.slots-card-thumb {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: none;
  background: #0d0c0a;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  transition: transform 0.12s ease, box-shadow 0.15s ease;
  aspect-ratio: 3 / 4;
  min-height: 96px;
}

.slots-card-thumb ::v-deep .game-thumb-wrap {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.slots-card-meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.4rem 0.1rem 0.35rem;
  min-height: 2.5rem;
}

.slots-card-name {
  font-size: 0.58rem;
  font-weight: 600;
  line-height: 1.25;
  color: #f0ebe3;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.slots-empty {
  text-align: center;
  padding: 3rem 1.5rem 2rem;
}

.slots-empty-icon {
  font-size: 2.25rem;
  color: var(--slot-gold-soft, #b09a5c);
  opacity: 0.35;
  margin-bottom: 0.75rem;
}

.slots-empty-text {
  font-size: 0.88rem;
  color: rgba(200, 192, 175, 0.75);
  margin: 0;
  line-height: 1.5;
}
</style>
